import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';
import DropdownButton, {
  DropdownButtonMenuItem,
  DropdownButtonProps,
} from '../DropdownButton';
import {
  ActionEnumType,
  ActionIconRecord,
  ActionItemPermission,
  ActionMenuRecord,
  ActionMenuType,
} from './types';

interface ActionMenuProps<
  T extends ActionEnumType,
  J extends ActionMenuRecord,
  K extends ActionIconRecord,
  L extends ActionItemPermission
> extends Pick<ActionMenuItemProps<J, K>, 'IconRecord' | 'MenuRecord'>,
    Pick<
      DropdownButtonProps,
      'iconFontSize' | 'classes' | 'aria-label' | 'disabled' | 'contentProps'
    > {
  id: string;
  actions: T;
  onClick: (action: keyof T) => void;
  PermissionsRecord: L;
  icon?: AvailableIconTypes;
  disabledTooltip?: ((children: React.ReactNode) => React.ReactNode) | null;
  portal?: boolean;
  actionMenuRef?: React.Ref<HTMLButtonElement>;
  tabIndex?: number;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
}

const useActionMenuItemStyles = makeStyles<{
  actionType: ActionMenuType;
  disabled?: boolean;
}>()((theme, { actionType, disabled }) => ({
  item: {
    color:
      actionType === ActionMenuType.destructive
        ? palette.semantic.text.error
        : 'inherit',
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'start',
    opacity: disabled ? 0.5 : 'none',
  },
}));

interface ActionMenuItemProps<
  T extends ActionMenuRecord,
  J extends ActionIconRecord
> extends DropdownButtonMenuItem {
  action: string;
  IconRecord: J;
  MenuRecord: T;
}

export const ActionMenuItem = <
  T extends ActionMenuRecord,
  J extends ActionIconRecord
>({
  action,
  IconRecord,
  MenuRecord,
  disabled,
  disabledTooltip,
}: ActionMenuItemProps<T, J>) => {
  const { classes } = useActionMenuItemStyles({
    actionType: MenuRecord[action],
    disabled,
  });
  const iconType = IconRecord[action];
  const content = (
    <span className={classes.item}>
      {iconType && <Icon type={iconType} />}
      <Typography variant="pSm">{action}</Typography>
    </span>
  );

  return (
    <Box className={classes.item}>
      {disabledTooltip && disabled ? disabledTooltip(content) : content}
    </Box>
  );
};

const useStyles = makeStyles()(theme => ({
  menu: {
    width: theme.spacing(20),
  },
}));

const ActionMenu = <
  T extends ActionEnumType,
  J extends ActionMenuRecord,
  K extends ActionIconRecord,
  L extends ActionItemPermission
>(
  props: ActionMenuProps<T, J, K, L>
) => {
  const {
    id,
    icon = 'moreHoriz',
    IconRecord,
    MenuRecord,
    actions,
    PermissionsRecord,
    onClick,
    classes: incomingClasses,
    iconFontSize,
    'aria-label': ariaLabel,
    portal,
    disabled,
    contentProps,
    actionMenuRef,
    tabIndex,
    onFocus,
  } = props;
  const { classes, cx } = useStyles();
  const items = Object.values(actions).filter(a => PermissionsRecord[a].show);
  return (
    <DropdownButton
      ref={actionMenuRef}
      id={id}
      iconType={icon}
      variant="icon"
      classes={{
        menuContent: cx(classes.menu, incomingClasses?.menuContent),
        iconButton: incomingClasses?.iconButton,
      }}
      iconFontSize={iconFontSize}
      aria-label={ariaLabel}
      portal={portal}
      disabled={disabled}
      contentProps={contentProps}
      tabIndex={tabIndex}
      onFocus={onFocus}
    >
      {items.map(f => (
        <ActionMenuItem<J, K>
          key={f}
          id={f}
          action={f}
          onClick={() => onClick(f)}
          IconRecord={IconRecord}
          MenuRecord={MenuRecord}
          disabled={PermissionsRecord[f].disabled}
          disabledTooltip={PermissionsRecord[f].disabledTooltip}
        />
      ))}
    </DropdownButton>
  );
};

export default ActionMenu;
